import { Link } from "react-router-dom";
import { ArrowRight, Banknote, CheckCircle, Clock, FileCheck, Shield, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

const steps = [
  {
    step: "01",
    icon: Shield,
    title: "Verify your identity",
    desc: "Upload your NRC or passport once. Takes under 2 minutes.",
    detail:
      "We use your government-issued ID to confirm your identity. This is a one-time step — once approved, you won't need to do it again for future loans.",
  },
  {
    step: "02",
    icon: Banknote,
    title: "Choose your loan",
    desc: "Pick how much you need and for how long. See your exact costs upfront.",
    detail:
      "Select an amount between K10,000 and K1,000,000 and a repayment term of 3 to 24 months. Your monthly payment and total interest are calculated instantly — no surprises.",
  },
  {
    step: "03",
    icon: FileCheck,
    title: "Submit your application",
    desc: "Fill in a few details about yourself and hit submit.",
    detail:
      "We ask for basic information like your employment status and income. The entire form takes about 3 minutes to complete.",
  },
  {
    step: "04",
    icon: Clock,
    title: "Get a decision",
    desc: "Our team reviews your application within 24 hours.",
    detail:
      "You'll receive a notification as soon as your application is reviewed. Most applications are processed the same business day.",
  },
  {
    step: "05",
    icon: Zap,
    title: "Receive your funds",
    desc: "Money is sent directly to your bank or mobile money account.",
    detail:
      "Once approved, funds are disbursed to your preferred account — Airtel Money, MTN Money, Zamtel Kwacha, or your bank account.",
  },
];

const faqs = [
  {
    q: "How long does the whole process take?",
    a: "From application to funds in your account, most borrowers are done within 24–48 hours.",
  },
  {
    q: "What documents do I need?",
    a: "Just a valid NRC, passport, or driver's license. No payslips or bank statements required for standard loans.",
  },
  {
    q: "Is there a fee to apply?",
    a: "No. Applying is completely free. You only pay interest on the loan once it's disbursed.",
  },
  {
    q: "Can I pay off my loan early?",
    a: "Yes. There are no early repayment penalties. You can settle your balance at any time.",
  },
];

export default function HowItWorksPage() {
  return (
    <div className="container mx-auto px-6 pb-20">
      {/* Hero */}
      <section className="mx-auto max-w-3xl pt-16 pb-12 text-center lg:pt-20">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
          How it works
        </p>
        <h1 className="text-3xl font-bold text-foreground font-display sm:text-4xl lg:text-5xl">
          From application to cash in{" "}
          <span className="bg-gradient-to-r from-primary to-emerald-600 bg-clip-text text-transparent">
            five simple steps
          </span>
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground leading-relaxed">
          No branch visits. No complicated paperwork. Everything happens online, on your schedule.
        </p>
      </section>

      {/* Steps */}
      <section className="mx-auto max-w-3xl space-y-6">
        {steps.map((s, i) => (
          <div
            key={s.step}
            className="group relative flex gap-6 rounded-2xl border border-border/50 bg-white p-6 shadow-sm transition-all hover:shadow-md hover:border-primary/20"
          >
            <div className="flex flex-col items-center">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                <s.icon className="h-6 w-6" />
              </div>
              {i < steps.length - 1 && (
                <div className="mt-3 h-full w-px bg-border" />
              )}
            </div>
            <div className="flex-1 pb-2">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground/50">
                Step {s.step}
              </p>
              <h3 className="mt-1 text-lg font-semibold text-foreground">
                {s.title}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                {s.detail}
              </p>
            </div>
          </div>
        ))}
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl pt-20">
        <h2 className="mb-8 text-center text-2xl font-bold text-foreground font-display">
          Common questions
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {faqs.map((f) => (
            <div
              key={f.q}
              className="rounded-2xl border border-border/40 bg-white p-5"
            >
              <h3 className="text-sm font-semibold text-foreground">{f.q}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {f.a}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-3xl pt-16 text-center">
        <div className="rounded-3xl bg-foreground px-8 py-12 text-background shadow-2xl">
          <h2 className="text-2xl font-bold font-display sm:text-3xl">
            Ready to get started?
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-background/70">
            Apply in under 5 minutes. See your costs before you commit.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="h-12 rounded-xl bg-white px-8 text-foreground shadow-lg hover:bg-white/90"
            >
              <Link to="/register">
                Start your application
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
